/**
 * Pure helpers backing the global undo/redo keyboard shortcuts (design doc
 * E17). Kept dependency-free (no DOM access beyond the plain values passed
 * in) so they're unit-testable without mounting the route that wires them
 * up via a `document` keydown listener — `matrices.$matrixId.tsx` needs a
 * live TanStack Router context to render, which the project's existing
 * testing convention (see `vitest.config.ts`'s `src/routes/**` coverage
 * exclusion) already treats as impractical to unit-test in isolation.
 */

/**
 * True when the given element is a text-editable control — an `<input>`, a
 * `<textarea>`, or a `contentEditable` region. While one is focused,
 * `Ctrl+Z`/`Ctrl+Shift+Z` must fall through to the browser's native text-undo
 * instead of triggering the matrix-level undo/redo (which would roll back the
 * previous matrix *action* on the server, out from under someone who was just
 * trying to undo their own typing).
 *
 * The `contentEditable` case matters because point 8 replaced the cell
 * editor's `<textarea>` with a `<div contentEditable>` (`matrix-grid.tsx`),
 * which a `tagName`-only check does not recognize.
 */
export function isEditableElement(element: Element | null): boolean {
  if (!element) return false;
  if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') return true;
  // `isContentEditable` is the authoritative answer in a real browser — it is
  // also true for a node *inside* an editable region, which the attribute
  // alone would miss. jsdom implements neither `contentEditable` reflection
  // nor `isContentEditable`, so fall back to the rendered attribute (which is
  // what React actually puts in the DOM) to stay testable.
  if (element instanceof HTMLElement && element.isContentEditable) return true;
  const contentEditableAttribute = element.getAttribute('contenteditable');
  return contentEditableAttribute !== null && contentEditableAttribute !== 'false';
}

export type UndoRedoShortcut = 'undo' | 'redo' | null;

interface ShortcutKeyEvent {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}

/**
 * Maps a keydown event to the shortcut it represents: `Ctrl+Z` (or `Cmd+Z`
 * on Mac) is undo, `Ctrl+Shift+Z`/`Cmd+Shift+Z` is redo. Returns `null` for
 * anything else.
 */
export function matchUndoRedoShortcut(event: ShortcutKeyEvent): UndoRedoShortcut {
  const hasModifier = event.ctrlKey || event.metaKey;
  if (!hasModifier || event.key.toLowerCase() !== 'z') return null;
  return event.shiftKey ? 'redo' : 'undo';
}
